import { InfoHighlight } from '@workday/wcp-cargo-react';
import * as React from 'react';
import { createContext, useContext } from 'react';
import { DevsiteCookies } from 'shared/models/cookies';
import { PackagePermission } from 'shared/models/user';
import { getCookie, setCookie } from 'modules/common/DevsiteCookies';
import useAsyncState from 'modules/common/hooks/useAsyncState';
import { CompanyUser, UserCompany, UserCompanyBase } from '../models/orgs';
import { OrgPermission } from '../models/permissions';
import { fetchOrg, getPrimaryOrg } from '../utils/orgs';
import { OrgProviderContent } from './OrgProviderContent';
import { ThreeDotsLoader } from './ThreeDotsLoader';

export type OrgLevelPermission = PackagePermission | OrgPermission;

export interface OrgProviderProps {
  orgs: UserCompanyBase[];
  refreshOrgs: (deleteExisting?: boolean) => void;
  hideAppWrapper?: boolean;
}

export interface OrgContextModel {
  currentOrg: UserCompany;
  /** Shows success and error toasts. Background switches skip the toasts and reject on failure instead. */
  setCurrentOrg: (org: UserCompanyBase, backgroundSwitch?: boolean) => Promise<void>;
  hasNoOrgs: boolean;
  refreshOrgs: (deleteExisting?: boolean) => void;
  isDsc: boolean;
  isOrgAdmin: boolean;
  isCollaborator: boolean;
  isDeveloper: boolean;
  isSpecialist: boolean;
  isMember: boolean;
  isPrimaryOrg: boolean;
  isAWS: boolean;
  getCurrentOrgUuid: () => string;
  getMember: (userUuid: string) => Promise<CompanyUser>;
  // checks that user has this permission or has EVERY one of the permissions passed in if multiple
  hasPermission: (permission: string | string[]) => boolean;
  // checks that user has this permission or has AT LEAST one of the permissions passed in if multiple
  hasSomePermission: (permission: string | string[]) => boolean;
  refreshOrgPermissions: () => void;
  orgs: UserCompanyBase[];
}

export const defaultOrgContext: OrgContextModel = {
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  currentOrg: null,
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  setCurrentOrg: async (_org) => null,
  hasNoOrgs: true,
  refreshOrgs: () => false,
  isDsc: false,
  isOrgAdmin: false,
  isCollaborator: false,
  isDeveloper: false,
  isSpecialist: false,
  isMember: false,
  isPrimaryOrg: false,
  isAWS: false,
  getCurrentOrgUuid: () => '',
  // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
  getMember: (_userUuid) => null,
  hasPermission: (_permission) => false,
  hasSomePermission: (_permission) => false,
  refreshOrgPermissions: () => undefined,
  orgs: [],
};

export const OrgContext = createContext<OrgContextModel>(defaultOrgContext);

const OrgProvider: React.FC<React.PropsWithChildren<OrgProviderProps>> = (props) => {
  const { orgs, refreshOrgs } = props;

  const [currentOrg, setCurrentOrg, currentOrgInitializer] = useAsyncState<UserCompany>(async () => {
    const orgUuid = getCookie(DevsiteCookies.WCP_ORG_UUID);
    const orgBase = orgs.find((org) => org.organizationUuid === orgUuid) || getPrimaryOrg(orgs);
    // @ts-expect-error DEVEXTOOLS-1514: fix strictNullChecks rule violation
    const org = await fetchOrg(orgBase, { trigger: 'Load' });

    // set it if it was never set
    setCookie(DevsiteCookies.WCP_ORG_UUID, org.organizationUuid);
    return org;
  });

  if (currentOrgInitializer.loading) return <ThreeDotsLoader />;

  if (currentOrgInitializer.error)
    return <InfoHighlight severity='ERROR'>Unable to fetch Company Information</InfoHighlight>;

  return (
    <OrgProviderContent orgs={orgs} refreshOrgs={refreshOrgs} currentOrg={currentOrg} setCurrentOrg={setCurrentOrg}>
      {props.children}
    </OrgProviderContent>
  );
};

interface HasOrgPermissionsProps {
  permissions?: OrgLevelPermission | OrgLevelPermission[];
}

export const HasOrgPermissions: React.FC<React.PropsWithChildren<HasOrgPermissionsProps>> = (props) => {
  const orgContext = useContext(OrgContext);

  return <>{(!props.permissions || orgContext.hasPermission(props.permissions)) && props.children}</>;
};

export const useOrgContext = () => useContext(OrgContext);

export default OrgProvider;
